import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import styles from "./Board.module.css";
import { BoardView } from "./BoardView";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button, ButtonLink } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Input } from "../../components/Input";
import { Segmented } from "../../components/Segmented";
import { usePreferences } from "../../data/api/preferences";
import { repoLabel, useRepository } from "../../data/api/repositories";
import { archiveTask, useRepoTasks } from "../../data/api/tasks";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { paths } from "../../routing/paths";
import { useNav } from "../../routing/useNav";

export const RepoView = () => {
  const { repoId = "" } = useParams();
  const nav = useNav();
  const { preferences, setLayout } = usePreferences();
  const {
    error: repoError,
    loading: repoLoading,
    repository,
  } = useRepository(repoId || null);
  const [filter, setFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const {
    loading: tasksLoading,
    refetch,
    tasks: allTasks,
  } = useRepoTasks(repoId || null, showArchived);
  // Live board: the server publishes task.updated / task.created on the repo
  // scope, so refetch the task list whenever the repo sees activity.
  useRealtimeScope(repoId ? `repo:${repoId}` : null, refetch);
  // The id stands in until the repo's name lands, so the browser tab is
  // identifiable from the first paint.
  useDocumentTitle(repository ? repoLabel(repository) : repoId);

  const tasks = useMemo(() => {
    if (!filter) {
      return allTasks;
    }
    const q = filter.toLowerCase();
    return allTasks.filter(
      (t) =>
        t.title.toLowerCase().includes(q) || t.id.toLowerCase().includes(q),
    );
  }, [allTasks, filter]);

  if (repoLoading && !repository) {
    return <div className={styles.loading}>Loading repository…</div>;
  }

  if (repoError || !repository) {
    return (
      <EmptyState
        body="This repository isn't connected, or the link is stale."
        title="Repository not found."
      />
    );
  }

  const repo = repository;
  const runAndRefetch =
    (fn: (id: string) => Promise<unknown>) =>
    (id: string): void => {
      void fn(id)
        .then(refetch)
        .catch(() => refetch());
    };
  const doArchive = runAndRefetch(archiveTask);

  const toggleSel = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const clearSel = (): void => setSelected(new Set());
  const archivedIds = showArchived ? tasks.map((t) => t.id) : [];

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.grow} />
        <Input
          className={styles.filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter tasks…"
          value={filter}
        />
        <Button
          onClick={() => setShowArchived((s) => !s)}
          size="sm"
          title={
            showArchived
              ? "Showing archived tasks — click to return to active"
              : "Show archived tasks"
          }
          variant={showArchived ? "primary" : "ghost"}
        >
          <Icon.Archive size={12} />{" "}
          {showArchived ? `Archived · ${tasks.length}` : "Archive"}
        </Button>
        <Segmented
          onValueChange={(v) => setLayout(v as "kanban" | "list")}
          options={[
            {
              label: (
                <>
                  <Icon.Kanban size={12} /> Kanban
                </>
              ),
              value: "kanban",
            },
            {
              label: (
                <>
                  <Icon.List size={12} /> List
                </>
              ),
              value: "list",
            },
          ]}
          value={preferences.layout}
        />
        <ButtonLink size="sm" to={paths.newAgent(repo.id)} variant="accent">
          <Icon.Plus size={13} /> New agent
        </ButtonLink>
      </div>

      {tasksLoading && allTasks.length === 0 ? (
        <div className={styles.loading}>Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <RepoEmpty
          filter={filter}
          newAgentHref={paths.newAgent(repo.id)}
          onClear={() => setFilter("")}
          showArchived={showArchived}
        />
      ) : (
        <BoardView
          archivedIds={archivedIds}
          layout={preferences.layout}
          onArchive={doArchive}
          onArchiveTask={doArchive}
          onOpen={(t) => nav.task(repo.id, t.displayId)}
          onToggleSel={toggleSel}
          selected={selected}
          showLiveFooter
          taskHref={(t) => paths.task(repo.id, t.displayId)}
          tasks={tasks}
        />
      )}

      {selected.size > 0 && (
        <div className={cx(styles.bulkBar, "u-fade-rise")}>
          <span className={styles.bulkCount}>{selected.size} selected</span>
          <span className={styles.grow} />
          <Button
            className={styles.bulkGhost}
            onClick={() => {
              selected.forEach((id) => doArchive(id));
              clearSel();
            }}
            size="sm"
            variant="ghost"
          >
            <Icon.Archive size={12} /> Archive
          </Button>
          <Button onClick={clearSel} size="sm">
            Done
          </Button>
        </div>
      )}
    </>
  );
};

type RepoEmptyProps = {
  readonly filter: string;
  readonly newAgentHref: string;
  readonly onClear: () => void;
  readonly showArchived: boolean;
};

const RepoEmpty = ({
  filter,
  newAgentHref,
  onClear,
  showArchived,
}: RepoEmptyProps) => {
  if (filter) {
    return (
      <EmptyState
        action={
          <Button onClick={onClear} size="sm">
            Clear filter
          </Button>
        }
        body={
          <>
            No tasks match <span className="mono">“{filter}”</span>. Try a
            looser search.
          </>
        }
        title="Nothing matches that."
      />
    );
  }
  if (showArchived) {
    return (
      <EmptyState
        body="Completed tasks you archive will collect here, out of the way."
        title="Archive is empty."
      />
    );
  }
  return (
    <EmptyState
      action={
        <ButtonLink size="sm" to={newAgentHref} variant="accent">
          <Icon.Plus size={13} /> Start an agent
        </ButtonLink>
      }
      body="No agents are working this repo yet. Describe a task and Tinsmith will pick up the hammer."
      title="The bench is clear."
    />
  );
};
